import { ShoppingBag, SquareArrowOutUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { ProductOptionGroup } from './ProductOptionGroup';
import { useChat } from '../context/ChatContext';
import { resolvePickedVariantSelection, sanitizeHtml } from '../lib/utils';
import { ProductPanelQuestionAsker } from './QuestionAsker/ProductPanelQuestionAsker';
import { useLocale } from '../context/LocaleContext';


interface Review {
  name: string;
  text: string;
}

interface ProductProps {
  id: string;
  title: string;
  price: string;
  compareAtPrice?: string;
  description: string;
  /** Optional rationale text for why this product was recommended */
  reason?: string;
  images: string[];
  /** Optional image metadata when backend provides ids */
  imageMeta?: Array<{ id: string; url: string }>;
  review: Review;
  searchId?: string;
  productHandle?: string;
  productUrl?: string;
  /** New variant model */
  variantDimensions?: string[];
  variantCombinations?: Array<{ values: string[]; sku?: string; url?: string; price?: number; compare_at_price?: number; image_ids?: string[] }>;
  pickedVariantValues?: string[];
}

export const ProductPanel: React.FC<ProductProps> = ({
  id,
  title,
  price,
  compareAtPrice,
  description,
  reason,
  images,
  imageMeta,
  review,
  productHandle,
  productUrl,
  variantDimensions,
  variantCombinations,
  pickedVariantValues,
  searchId,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { onAddToCart, onNavigateToProduct, onReadMore, setCurrentProduct } = useChat();
  const [adding, setAdding] = useState(false);

  // Ensure backend requests from inside the panel can be scoped to this product.
  useEffect(() => {
    setCurrentProduct(id);
    return () => setCurrentProduct(null);
  }, [id, setCurrentProduct]);

  const dims = useMemo(() => (Array.isArray(variantDimensions) ? variantDimensions.slice(0, 3) : []), [variantDimensions]);
  const combos = useMemo(
    () => (Array.isArray(variantCombinations) ? variantCombinations.filter(c => Array.isArray(c?.values) && c.values.length === dims.length) : []),
    [variantCombinations, dims.length]
  );

  const normalizeKey = useCallback((name: string): string => {
    const lowered = name.trim().toLowerCase();
    const noDiacritics = lowered.normalize('NFD').replace(/\p{Diacritic}/gu, '');
    return noDiacritics.replace(/\s+/g, '_');
  }, []);

  const getOptionsForIndex = useCallback((index: number, prefix: string[]): string[] => {
    const set = new Set<string>();
    for (const c of combos) {
      let ok = true;
      for (let i = 0; i < prefix.length; i++) {
        if (c.values[i] !== prefix[i]) { ok = false; break; }
      }
      if (!ok) continue;
      set.add(c.values[index]);
    }
    return Array.from(set);
  }, [combos]);

  const findExact = useCallback((values: string[]) => combos.find(c => c.values.every((v, i) => v === values[i])), [combos]);

  const deriveCascade = useCallback((): string[] => {
    const selection: string[] = [];
    for (let i = 0; i < dims.length; i++) {
      const opts = getOptionsForIndex(i, selection);
      selection[i] = opts[0] ?? '';
    }
    return selection;
  }, [dims.length, getOptionsForIndex]);

  const { formatMoney, t } = useLocale();
  const formatPrice = useCallback((amount?: number): string => {
    return formatMoney(amount);
  }, [formatMoney]);

  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [displayPrice, setDisplayPrice] = useState<string>(price);
  const [displayCompareAt, setDisplayCompareAt] = useState<string | undefined>(compareAtPrice);

  const selectedCombo = useMemo(() => {
    if (dims.length === 0 || combos.length === 0) return undefined;
    if (selectedValues.length !== dims.length) return undefined;
    return findExact(selectedValues);
  }, [dims.length, combos.length, selectedValues, findExact]);

  const visibleImages = useMemo(() => {
    const imageIdsRaw = (selectedCombo as any)?.image_ids;
    const imageIds = Array.isArray(imageIdsRaw)
      ? imageIdsRaw.filter((id: unknown): id is string => typeof id === "string" && id.length > 0)
      : [];

    // No image ids for this variant => show all images
    if (imageIds.length === 0) return images;

    // Can't safely filter without id->url metadata => show all images
    if (!Array.isArray(imageMeta) || imageMeta.length === 0) return images;

    const idSet = new Set(imageIds);
    const filtered = imageMeta.filter((img) => idSet.has(img.id)).map((img) => img.url);

    // Variants never intentionally have zero images; prefer all images on mismatch
    return filtered.length > 0 ? filtered : images;
  }, [images, imageMeta, selectedCombo]);

  // Initialize selection using persisted, picked, or cascade
  useEffect(() => {
    if (dims.length === 0 || combos.length === 0) {
      setSelectedValues([]);
      setDisplayPrice(price);
      return;
    }

    const resolvedPicked = resolvePickedVariantSelection(dims, combos, pickedVariantValues);
    const target = resolvedPicked ?? deriveCascade();

    setSelectedValues(target);
    const combo = findExact(target);
    setDisplayPrice(formatPrice(combo?.price) || price);
    setDisplayCompareAt(formatPrice((combo as any)?.compare_at_price) || undefined);
  }, [dims, combos, pickedVariantValues, findExact, deriveCascade, formatPrice, price]);

  const handleChangeAt = useCallback((index: number, value: string) => {
    setSelectedValues((prev) => {
      const next = prev.slice(0, index);
      next[index] = value;
      // cascade recompute subsequent selections
      for (let j = index + 1; j < dims.length; j++) {
        const opts = getOptionsForIndex(j, next);
        next[j] = opts[0] ?? '';
      }
      // update price
      const combo = findExact(next);
      setDisplayPrice(formatPrice(combo?.price) || price);
      setDisplayCompareAt(formatPrice((combo as any)?.compare_at_price) || undefined);
      return next;
    });
  }, [dims, getOptionsForIndex, findExact, formatPrice, price]);

  const buildSelectedVariantMap = useCallback((): Record<string, string> => {
    const map: Record<string, string> = {};
    for (let i = 0; i < dims.length; i++) {
      const name = dims[i];
      const key = normalizeKey(name);
      const val = selectedValues[i];
      if (key && typeof val === 'string') map[key] = val;
    }
    return map;
  }, [dims, selectedValues, normalizeKey]);

  const handleAddToCart = useCallback(async () => {
    const selected_variant = buildSelectedVariantMap();
    setAdding(true);
    try {
      await onAddToCart({
        product: {
          id,
          title,
          price: displayPrice,
          images,
          description,
          ...(productHandle ? { productHandle } : {}),
          ...(productUrl ? { productUrl } : {}),
        },
        selectedVariant: selected_variant,
        quantity: 1,
        searchId,
      });
    } finally {
      setAdding(false);
    }
  }, [buildSelectedVariantMap, onAddToCart, id, title, images, description, searchId, displayPrice, productHandle, productUrl]);

  const handleNavigate = useCallback(async () => {
    const selected_variant = buildSelectedVariantMap();
    
    // Fire-and-forget tracking call (don't await to avoid blocking navigation)
    onReadMore({
      product: {
        id,
        title,
        price: displayPrice,
        images,
        description,
        ...(productHandle ? { productHandle } : {}),
        ...(productUrl ? { productUrl } : {}),
      },
      searchId,
    }).catch(() => {
      // Silently ignore errors - tracking should never break UX
    });
    
    await onNavigateToProduct({
      product: {
        id,
        title,
        price: displayPrice,
        images,
        description,
        ...(productHandle ? { productHandle } : {}),
        ...(productUrl ? { productUrl } : {}),
      },
      selectedVariant: selected_variant,
      target: '_blank',
      searchId,
    });
  }, [buildSelectedVariantMap, onNavigateToProduct, onReadMore, id, title, images, description, displayPrice, searchId, productHandle, productUrl]);

  // Carousel state and logic
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [imageLoads, setImageLoads] = useState(0); // Track loaded images

  // Reset carousel state when the visible image set changes
  useEffect(() => {
    setImageLoads(0);
    setCanScrollLeft(false);
    setCanScrollRight(false);
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0;
    }
  }, [visibleImages]);

  // Effect for scroll listening and initial checks
  useEffect(() => {
    const checkScrollLimits = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
      }
    };

    checkScrollLimits();
    const currentRef = scrollRef.current;
    currentRef?.addEventListener('scroll', checkScrollLimits, { passive: true });
    return () => currentRef?.removeEventListener('scroll', checkScrollLimits);
  }, [visibleImages]);

  // New effect: Re-check limits after images load or on timeout
  useEffect(() => {
    const checkScrollLimits = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
      }
    };

    checkScrollLimits(); // Check when imageLoads changes
  }, [imageLoads]);

  // One-time timeout check on mount
  useEffect(() => {
    const checkScrollLimits = () => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        setCanScrollLeft(scrollLeft > 0);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
      }
    };

    const timeout = setTimeout(checkScrollLimits, 500);
    return () => clearTimeout(timeout);
  }, []); // Empty dependency: runs once on mount

  const scrollToDirection = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      const scrollAmount = direction === 'left' ? -clientWidth : clientWidth;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="flex flex-col h-full bg-white overflow-auto">
      {/* Horizontal image carousel with relative aspect ratio */}
      <div className="w-full relative bg-gray-50 overflow-hidden flex-shrink-0" style={{ paddingBottom: '75%' }}> {/* 4:3 aspect ratio */}
        <div
          ref={scrollRef}
          className="absolute inset-0 flex flex-row overflow-x-auto snap-x snap-mandatory"
          style={{
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE/Edge
          }}
        >
          {/* WebKit scrollbar hiding */}
          <style>{`
            div[ref="scrollRef"]::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          {visibleImages.length === 0 ? (
            <div className="flex items-center justify-center h-full w-full text-gray-500">
              {t("product.no_images")}
            </div>
          ) : (
            visibleImages.map((src, index) => (
              <img
                key={index}
                src={src}
                alt={`${title} - image ${index + 1}`}
                className="h-full w-full basis-full flex-shrink-0 object-contain snap-start"
                loading={index === 0 ? 'eager' : 'lazy'}
                onLoad={() => setImageLoads((prev) => prev + 1)}
              />
            ))
          )}
        </div>

        {/* Navigation arrows - only show if multiple images */}
        {visibleImages.length > 1 && (
          <>
            <button
              onClick={() => scrollToDirection('left')}
              disabled={!canScrollLeft}
              aria-label="Previous image"
              className={`absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full transition-colors ${
                !canScrollLeft ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white'
              }`}
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => scrollToDirection('right')}
              disabled={!canScrollRight}
              aria-label="Next image"
              className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full transition-colors ${
                !canScrollRight ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white'
              }`}
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>

      {/* Product info */}
      <div className="p-8 flex-1 flex flex-col text-left">
        <h2 className="text-[24px] font-bold text-left">{title}</h2>
        <p className="text-[24px] mt-1 text-left">
          <span>{displayPrice || price}</span>
          {displayCompareAt && (displayCompareAt !== (displayPrice || price)) && (
            <span className="text-[14px] text-gray-500 line-through ml-2 align-middle">{displayCompareAt}</span>
          )}
        </p>
        {/* Variant option rows */}
        {dims.map((dimName, i) => {
          const options = getOptionsForIndex(i, selectedValues.slice(0, i));
          const selected = selectedValues[i];
          return (
            <ProductOptionGroup
              key={dimName}
              name={dimName}
              options={options}
              selected={selected}
              onChange={(val) => handleChangeAt(i, val)}
            />
          );
        })}
        
        {/* Reason display (styled like assistant messages) */}
        {reason && (
          <div className="mt-6 text-left border rounded-lg p-4" style={{
            borderColor: 'var(--secondary)',
            fontFamily: 'var(--font-body)',
            color: 'var(--text-secondary)',
            fontSize: '16px'
          }}>
            {reason}
          </div>
        )}

        <div 
          className="mt-6 text-gray-700 text-left"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
        />

        {/* Product-specific Q&A (detached from main Browser transcript) */}
        <div className="mt-6">
          <ProductPanelQuestionAsker productId={id} />
        </div>
        
        {/* Action buttons */}
        <div className="mt-auto pt-6 grid gap-4">
          <button 
            className="ww-btn ww-btn-primary w-full"
            onClick={handleNavigate}
          >
            <SquareArrowOutUpRight size={20} />
            {t("product.read_more")}
          </button>
        </div>
      </div>
    </div>
  );
}; 